import { useParams } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";
import { findGame, findProvider } from "@/data/providers";
import { listCasinos } from "@/data/casinos";
import { CasinoCard } from "@/components/CasinoCard";
import { NotFoundPage } from "./NotFoundPage";
import { providerPath } from "@/lib/format";

export function SlotPage() {
  const { slot = "" } = useParams<{ slot: string }>();
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const game = findGame(slot);
  if (!game) return <NotFoundPage />;
  const provider = findProvider(game.provider_slug);
  const casinos = listCasinos({ provider: game.provider_slug });

  return (
    <div className="container-page py-8">
      <SEO
        title={isSv ? `${game.name} – spelautomat` : `${game.name} – slot`}
        description={isSv ? game.description_sv : game.description_en}
      />
      <Breadcrumbs
        items={[
          { label: t("nav.home"), href: prefix },
          {
            label: isSv ? "Spelautomater" : "Slots",
            href: `${prefix}/${isSv ? "spelautomater" : "slots"}`,
          },
          { label: game.name },
        ]}
      />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{game.name}</h1>
      {provider && (
        <p className="mt-1 text-sm text-ink-600">
          {isSv ? "Spelleverantör:" : "Provider:"}{" "}
          <a className="text-brand-700" href={providerPath(prefix, lang, provider.slug)}>
            {provider.name}
          </a>
        </p>
      )}
      <p className="mt-2 max-w-3xl text-ink-700">
        {isSv ? game.description_sv : game.description_en}
      </p>

      <div className="mt-4 flex gap-2">
        {typeof game.rtp === "number" && (
          <span className="pill-brand">RTP {game.rtp.toFixed(2)}%</span>
        )}
        {game.volatility && (
          <span className="pill-brand">{isSv ? "Volatilitet" : "Volatility"}: {game.volatility}</span>
        )}
        <span className="pill">{game.type}</span>
      </div>

      <h2 className="mt-8 text-2xl font-bold">
        {isSv ? `Spela ${game.name} hos:` : `Play ${game.name} at:`}
      </h2>
      <div className="mt-4 grid gap-4">
        {casinos.length === 0 ? (
          <p className="card p-6 text-ink-600">{t("list.empty")}</p>
        ) : (
          casinos.map((c, i) => (
            <CasinoCard key={c.id} casino={c} rank={i + 1} source={`slot-${game.slug}`} />
          ))
        )}
      </div>

      <InternalLinks context="slot" />
    </div>
  );
}
